import React, { useState } from 'react';
import useToggleAction from '@/hooks/useToggleAction';
import { db } from '@root/firebase';

/**
 * LikeButton
 *
 * Props:
 * - collectionPath: string (Firestore collection path, e.g. "files/library/files")
 * - documentId: string (optional; required if targeting a single doc)
 * - userId: string (who is liking)
 * - className: string (extra classes for styling)
 * - buttonSize: 'xs' | 'sm' | undefined  (maps to "general-btn-{size}")
 *
 * Notes:
 * - If documentId is provided → toggles like on that document.
 * - If documentId is omitted → toggles like on a generated user doc inside the collection.
 */
function LikeButton({ collectionPath, documentId, userId, className = '' }) {
  const { state: isLiked, toggleAction } = useToggleAction(
    db,
    collectionPath,
    documentId,
    'liked',
    userId
  );

  const [busy, setBusy] = useState(false);

  const classes = [
    'reaction-btn',
    'reaction-like',
    isLiked ? 'is-active' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const canClick = !!userId && !busy;

  const handleClick = async () => {
    if (!canClick) return;
    try {
      setBusy(true);
      await toggleAction();
    } finally {
      setBusy(false);
    }
  };

  const titleText = !userId ? 'Sign in to like' : isLiked ? 'Unlike' : 'Like';

  return (
    <div
      className='tooltip-container'
      style={{ display: 'inline-block', marginRight: '10px' }}
    >
      <button
        type='button'
        className={classes}
        title={titleText}
        aria-label={titleText}
        aria-pressed={isLiked}
        disabled={!canClick}
        onClick={handleClick}
      >
        {/* Heart icon */}
        <svg
          className='reaction-icon'
          width='18'
          height='18'
          viewBox='0 0 24 24'
          aria-hidden='true'
          focusable='false'
        >
          <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4c1.74 0 3.41 1.01 4.13 2.44h.74C14.09 5.01 15.76 4 17.5 4 20 4 22 6 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
        </svg>
      </button>
      <span className='tooltip'>{titleText}</span>
    </div>
  );
}

export default LikeButton;
